import React, { useMemo } from 'react';

import { ScatterChart } from 'echarts/charts';
import * as echarts from 'echarts/core';

import { axisScale, referenceMarks, scaleBounds } from '../axisStyle';

const titled = (block: Block | undefined, key: 'xTitle' | 'yTitle', fallback: string) => (typeof block?.[key] === 'string' && block[key] ? String(block[key]) : fallback);
import { formatAxis, formatValue, unitOf } from '../format';
import type { Palette } from '../palette';
import type { Block, Envelope, EnvelopeColumn } from '../types';

import Chart, { type ChartOption } from './Chart';

echarts.use([ScatterChart]);

type ScatterProps = { block?: Block; envelope: Envelope; palette: Palette };

const LABELLED = 8;

export function scatterOption({ block, envelope, palette }: ScatterProps): ChartOption {
  const [x, y] = envelope.columns.filter((column) => column.role === 'measure') as Array<EnvelopeColumn>;
  const dim = envelope.columns.find((column) => column.role === 'dim');
  const points = envelope.rows
    .map((row) => ({ name: dim ? String(row[dim.name]) : '', value: [row[x.name] as number | null, row[y.name] as number | null] }))
    .filter((point) => point.value[0] !== null && point.value[1] !== null);
  const named = new Set([...points].sort((a, b) => Math.abs(b.value[0] as number) - Math.abs(a.value[0] as number)).slice(0, LABELLED).map((point) => point.name));
  return {
    animation: false,
    textStyle: { fontFamily: palette.sans, fontSize: 11, color: palette.ink3 },
    grid: { left: 8, right: 24, top: 32, bottom: 24, containLabel: true },
    tooltip: {
      backgroundColor: palette.surface,
      borderColor: palette.hairlineStrong,
      textStyle: { color: palette.ink, fontSize: 12, fontFamily: palette.sans },
      formatter: (item: { name: string; value: [number, number] }) => `${item.name}<br/>${x.label}: ${formatValue(item.value[0], x.format)}<br/>${y.label}: ${formatValue(item.value[1], y.format)}`,
    },
    xAxis: { type: 'value', triggerEvent: true, name: titled(block, 'xTitle', unitOf(x.format) ? `${x.label} (${unitOf(x.format)})` : x.label), nameLocation: 'middle', nameGap: 22, nameTextStyle: { color: palette.ink3, fontSize: 11 }, axisLabel: { color: palette.ink3, fontSize: 11, formatter: (value: number) => formatAxis(value, x.format) }, splitLine: { lineStyle: { color: palette.hairline } }, axisLine: { lineStyle: { color: palette.hairlineStrong } } },
    yAxis: { type: 'value', triggerEvent: true, splitNumber: 4, ...scaleBounds(axisScale(block)), name: titled(block, 'yTitle', unitOf(y.format) ? `${y.label} (${unitOf(y.format)})` : y.label), nameTextStyle: { color: palette.ink3, fontSize: 11, align: 'left' }, axisLabel: { color: palette.ink3, fontSize: 11, formatter: (value: number) => formatAxis(value, y.format) }, splitLine: { lineStyle: { color: palette.hairline } } },
    series: [
      {
        type: 'scatter',
        symbolSize: 7,
        itemStyle: { color: palette.series[0], opacity: 0.85 },
        data: points,
        label: { show: true, position: 'right', fontSize: 10.5, color: palette.ink2, formatter: (item: { name: string }) => (named.has(item.name) ? item.name : '') },
        labelLayout: { hideOverlap: true },
        ...referenceMarks(block, palette, y.format),
      },
    ],
  };
}

export default function Scatter({ block, envelope, palette }: ScatterProps) {
  const option = useMemo(() => scatterOption({ block, envelope, palette }), [block, envelope, palette]);
  return <Chart option={option} />;
}
